import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";


const SCRIPT_SRC = "https://ca.turing.captcha.qcloud.com/TJNCaptcha-global.js";

function TencentCaptcha({ appId, onVerify, onError }) {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const containerRef = useRef(null);           // where the widget will render
  const instanceRef = useRef(null);            // store captcha instance

   useEffect(() => {
    if (typeof window === "undefined") return;

    // If SDK already loaded
    if (window.TencentCaptcha) {
      setScriptLoaded(true);
      return;
    }

    // If script tag already exists, wait for it
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => setScriptLoaded(true));
      return;
    }

    // Otherwise create script tag
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      setScriptLoaded(true);
    };
    script.onerror = () => {
      onError?.(new Error("Failed to load Tencent CAPTCHA script"));
    };
    document.body.appendChild(script);
  }, [onError]);

  useEffect(() => {
    if (!scriptLoaded) return;
    if (typeof window === "undefined") return;
    if (!window.TencentCaptcha) return;
    if (!containerRef.current) return;

    const instance = new window.TencentCaptcha(
      containerRef.current,    // DOM container for the widget
      appId,
      (res) => {

        if (res?.ret === 0 && !res?.errorCode) {
          onVerify?.(res.ticket, res.randstr);
          return;
        }

        if (res?.errorCode) {
          // internal error (e.g. frame load issue)
          onError?.(
            new Error(
              `Tencent CAPTCHA error ${res.errorCode}: ${
                res.errorMessage || ""
              }`
            )
          );
          return;
        }
      },
      {} // options if you need (lang, bizState, etc.)
    );

    instance.show(); // to render the "I am human" box
    instanceRef.current = instance;

    // cleanup if component unmounts
    return () => {
      instanceRef.current?.destroy?.();
      instanceRef.current = null;
    };
  }, [scriptLoaded, appId, onVerify, onError]);

  return (
    <div style={{ marginTop: "20px" }}>
      <div ref={containerRef} />
      {!scriptLoaded && <p>Loading CAPTCHA…</p>}
    </div>
  );
}

TencentCaptcha.propTypes = {
  appId: PropTypes.string.isRequired,
  onVerify: PropTypes.func,
  onError: PropTypes.func,
};

export default TencentCaptcha;
